import { DocumentNode, gql } from '@apollo/client';

const getEvents = gql`
  query GetEvents($page: Int, $size: Int, $filters: EventFilters) {
    items: getEvents(page: $page, size: $size, filters: $filters) {
      id
      name
      slug
      status
      category
      hidden
      startDate
      endDate
      countSubEvents
      users {
        id
        name
      }
    }
    total: _getEventsMeta(filters: $filters) {
      count
    }
  }
`;

const getEventsStatusCounts = gql`
  query GetEventsStatusCounts {
    _getEventsStatusCounts {
      all
      live
      upcoming
      finished
    }
  }
`;

const getEvent = gql`
  query getEvent($id: String!) {
    item: getEvent(slug: $id) {
      name
      slug
      category
      location {
        lat
        lng
      }
      zoom
      startDate
      endDate
      publicDate
      isOpen
      hidden
      description
      web
      domaMapId
      domaUrl
      createdAt
      updatedAt

      settings {
        canEnter
        autoAcceptEntry
        autoCreateCompetitorsFromActivity
        analytic
        useSI
        useResults
        liveResultsCompetitionId
        delay
        gpsInterval
      }

      subEvents {
        id
        name
        sub
        useGPS
        public
      }
      competitors {
        id
        name
        shortName
        startTime
        color
        subEvent {
          name
        }
        siCard
        device {
          id
          imei
          name
        }
        user {
          id
          firstName
          lastName
        }
      }
      maps {
        id
        name
        url
        createdAt
        show
        topLeft {
          lat
          lng
        }
        topRight {
          lat
          lng
        }
        bottomLeft {
          lat
          lng
        }
      }
      entries {
        id
        status
        createdAt
        user {
          firstName
          lastName
        }
        subEvent {
          id
          name
        }
      }
    }
  }
`;

const getEventSettings = gql`
  query getEventSettings($id: String!) {
    item: getEventSettings(slug: $id) {
      canEnter
      autoAcceptEntry
      autoCreateCompetitorsFromActivity
      analytic
      useSI
      useResults
      liveResultsCompetitionId
      delay
      gpsInterval
      altEventSettings
      markerType
    }
  }
`;

const updateEvent = gql`
  mutation UpdateEvent($id: String!, $payload: EventPayload) {
    item: updateEvent(slug: $id, payload: $payload) {
      name
    }
  }
`;

const updateEventSettings = gql`
  mutation UpdateEventSettings($id: String!, $payload: EventSettingsPayload) {
    item: updateEventSettings(slug: $id, payload: $payload) {
      name
    }
  }
`;

const createEvent = gql`
  mutation CreateEvent($payload: EventPayload!) {
    item: createEvent(payload: $payload) {
      name
    }
  }
`;

const deleteEvent = gql`
  mutation DeleteMyEvent($id: String!) {
    item: deleteMyEvent(slug: $id) {
      name
    }
  }
`;

const getSubEvents = gql`
  query GetSubEvents($slug: String!, $page: Int, $size: Int) {
    items: getSubEvents(slug: $slug, page: $page, size: $size) {
      id
      name
      sub
      useGPS
      public
    }
    total: _getSubEventsMeta(slug: $slug) {
      count
    }
  }
`;

const createSubEvent = gql`
  mutation CreateSubEvent($slug: String!, $payload: SubEventPayload!) {
    item: createSubEvent(slug: $slug, payload: $payload) {
      id
      name
      sub
      useGPS
      public
    }
  }
`;

const updateSubEvent = gql`
  mutation UpdateSubEvent($slug: String!, $payload: SubEventPayload!) {
    item: updateSubEvent(slug: $slug, payload: $payload) {
      id
      name
      sub
      useGPS
      public
    }
  }
`;

const deleteSubEvent = gql`
  mutation DeleteSubEvent($slug: String!, $id: Int!) {
    item: deleteSubEvent(slug: $slug, id: $id) {
      name
    }
  }
`;

const getSubEvent = gql`
  query GetSubEvent($id: String!) {
    item: getSubEvent(subEventId: $id) {
      id
      name
      sub
      useGPS
      public
      liveloxUrl
      event {
        name
        slug
        startDate
        status
        settings {
          useSI
        }
        location {
          lat
          lng
        }
        category
        zoom
        controls {
          id
          name
          type
          show
          analytic
          position {
            lat
            lng
          }
        }
      }
      controls {
        id
        name
        type
        createdAt
        position {
          lat
          lng
        }
        show
        analytic
      }
      maps {
        id
        name
        url
        createdAt
        show
        topLeft {
          lat
          lng
        }
        topRight {
          lat
          lng
        }
        bottomLeft {
          lat
          lng
        }
      }
      routes {
        id
        name
        show
        color
        track {
          lat
          lng
        }
      }
      competitors {
        id
        name
        shortName
        startTime
        color
        siCard
        device {
          id
          imei
          name
        }
        user {
          id
          firstName
          lastName
        }
      }
    }
    getDevices {
      id
      name
      isVirtual
    }
  }
`;

const getControl = gql`
  query GetControl($slug: String!, $id: Int!) {
    item: getControl(slug: $slug, id: $id) {
      id
      name
      type
      position {
        lat
        lng
      }
      show
      analytic
    }
  }
`;

const getControls = gql`
  query GetControls($slug: String!) {
    items: getControls(slug: $slug) {
      id
      name
      type
      show
      analytic
      position {
        lat
        lng
      }
    }
    total: _getControlsMeta(slug: $slug) {
      count
    }
  }
`;

const createControl = gql`
  mutation CreateControl($slug: String!, $payload: ControlPayload!) {
    item: createControl(slug: $slug, payload: $payload) {
      name
    }
  }
`;

const createManyControls = gql`
  mutation CreateManyControls($slug: String!, $payload: ControlManyPayload!) {
    items: createManyControls(slug: $slug, payload: $payload) {
      name
    }
  }
`;

const deleteControl = gql`
  mutation DeleteControl($slug: String!, $id: Int!) {
    item: deleteControl(slug: $slug, id: $id) {
      name
    }
  }
`;

const deleteManyControls = gql`
  mutation DeleteManyControls($slug: String!, $ids: [Int!]) {
    item: deleteManyControls(slug: $slug, ids: $ids) {
      name
    }
  }
`;

const updateControl = gql`
  mutation UpdateControl($slug: String!, $id: Int!, $payload: ControlPayload!) {
    item: updateControl(slug: $slug, id: $id, payload: $payload) {
      name
    }
  }
`;

const updateManyControls = gql`
  mutation UpdateManyControls($slug: String!, $ids: [Int!], $payload: ControlPayload!) {
    items: updateManyControls(slug: $slug, ids: $ids, payload: $payload) {
      name
    }
  }
`;

const reorderControls = gql`
  mutation ReorderControls($slug: String!, $payload: [ReorderPayload!]!) {
    items: reorderControls(slug: $slug, payload: $payload) {
      name
    }
  }
`;

const getSubEventControls = gql`
  query GetSubEventControls($subEventId: Int!) {
    items: getSubEventControls(subEventId: $subEventId) {
      id
      name
      type
      show
      analytic
      order
    }
  }
`;

const updateManySubEventControls = gql`
  mutation UpdateManySubEventControls($subEventId: Int!, $payload: [ControlPayload!]!) {
    items: updateManySubEventControls(subEventId: $subEventId, payload: $payload) {
      name
    }
  }
`;

const mergeControl = gql`
  mutation MergeControl($slug: String!, $id: Int!, $targetId: Int!) {
    item: mergeControl(slug: $slug, id: $id, targetId: $targetId) {
      id
      name
    }
  }
`;

export const getUserRecentActivities = gql`
  query UserRecentActivities($userId: Int!) {
    userRecentActivities(userId: $userId) {
      garmin {
        id
        externalId
        name
        activityType
        startTimeInSeconds
        durationInSeconds
      }
      suunto {
        id
        externalId
        name
        activityType
        startTimeInSeconds
        durationInSeconds
      }
      coros {
        id
        externalId
        name
        activityType
        startTimeInSeconds
        durationInSeconds
      }
    }
  }
`;

export const getAllUsers = gql`
  query GetAllUsers($page: Int, $size: Int, $filters: UserFilters) {
    items: allUsers(page: $page, size: $size, filters: $filters) {
      id
      firstName
      lastName
      name
      email
      active
      block
      hasStrava
      hasGarmin
      hasSuunto
      hasCoros
      city
      club
      siCard
      createdAt
      updatedAt
      role {
        id
        name
      }
      roles {
        id
        name
      }
      userGroups {
        id
        name
      }
      preferredDevices {
        shortName
        color
        device {
          id
          imei
          name
        }
      }
      userDevice {
        id
        imei
        name
      }
    }
    total: _allUsersMeta(filters: $filters) {
      count
    }
  }
`;

const updateAllUser = gql`
  mutation UpdateAllUser($id: Int!, $payload: UserPayload!) {
    item: updateUser(id: $id, payload: $payload) {
      name
    }
  }
`;

export const getGroups = gql`
  query GetGroups {
    items: getGroups {
      id
      name
    }
  }
`;

const getCompetitorTrack = gql`
  query GetCompetitorTrack($subEventId: Int!, $id: Int!) {
    item: getCompetitorTrack(subEventId: $subEventId, id: $id) {
      track {
        lat
        lng
        time
        alt
      }
    }
  }
`;

const createCompetitorTrack = gql`
  mutation CreateCompetitorTrack($subEventId: Int!, $id: Int!, $payload: CompetitorTrackPayload) {
    item: createCompetitorTrack(subEventId: $subEventId, id: $id, payload: $payload) {
      name
    }
  }
`;

const updateCompetitorTrack = gql`
  mutation UpdateCompetitorTrack($subEventId: Int!, $id: Int!, $payload: CompetitorTrackPayload) {
    item: updateCompetitorTrack(subEventId: $subEventId, id: $id, payload: $payload) {
      name
    }
  }
`;

const deleteCompetitorTrack = gql`
  mutation DeleteCompetitorTrack($subEventId: Int!, $id: Int!) {
    item: deleteCompetitorTrack(subEventId: $subEventId, id: $id)
  }
`;

const getCompetitor = gql`
  query GetCompetitor($subEventId: Int!, $id: Int!) {
    getCompetitor(subEventId: $subEventId, id: $id) {
      id
      name
      shortName
      startTime
      color
      siCard
      device {
        id
        imei
        name
      }
      deviceId
      user {
        id
        firstName
        lastName
      }
      userId
    }
  }
`;

const getCompetitors = gql`
  query GetCompetitors($subEventId: Int!) {
    items: getCompetitors(subEventId: $subEventId) {
      id
      name
      shortName
      startTime
      color
      siCard
      device {
        id
        imei
        name
      }
      deviceId
      user {
        id
        firstName
        lastName
      }
      userId
      hasTrack
      isVirtual
      isPrivate
    }
    total: _getCompetitorsMeta(subEventId: $subEventId) {
      count
    }
  }
`;

const createCompetitor = gql`
  mutation CreateCompetitor($subEventId: Int!, $payload: CompetitorPayload!) {
    item: createCompetitor(subEventId: $subEventId, payload: $payload) {
      id
      name
      hasTrack
      device {
        id
        name
      }
    }
  }
`;

const createManyCompetitors = gql`
  mutation CreateManyCompetitors($subEventId: Int!, $payload: CompetitorManyPayload!) {
    items: createManyCompetitors(subEventId: $subEventId, payload: $payload) {
      name
    }
  }
`;

const updateCompetitor = gql`
  mutation UpdateCompetitor($subEventId: Int!, $payload: CompetitorPayload!) {
    item: updateCompetitor(subEventId: $subEventId, payload: $payload) {
      name
    }
  }
`;

const deleteCompetitor = gql`
  mutation DeleteCompetitor($subEventId: Int!, $id: Int!) {
    item: deleteCompetitor(subEventId: $subEventId, id: $id) {
      name
    }
  }
`;

const deleteManyCompetitors = gql`
  mutation DeleteManyCompetitors($subEventId: Int!, $ids: [Int!]) {
    item: deleteManyCompetitors(subEventId: $subEventId, ids: $ids) {
      name
    }
  }
`;

const reorderCompetitors = gql`
  mutation ReorderCompetitors($subEventId: Int!, $payload: [ReorderPayload!]!) {
    items: reorderCompetitors(subEventId: $subEventId, payload: $payload) {
      name
    }
  }
`;

const updateCompetitorPrivateCompetitor = gql`
  mutation UpdateCompetitorPrivate($subEventId: Int!, $id: Int!, $isPrivate: Boolean!) {
    item: updateCompetitorPrivate(subEventId: $subEventId, id: $id, isPrivate: $isPrivate) {
      id
      isPrivate
    }
  }
`;

const getEntries = gql`
  query GetEntries($filters: EntriesFilters) {
    items: getEntries(filters: $filters) {
      id
      status
      createdAt
      userName
      subEvent {
        name
        event {
          name
        }
      }
    }
    total: _getEntriesMeta(filters: $filters) {
      count
    }
  }
`;

const updateEntry = gql`
  mutation UpdateEntry($payload: EntryPayload!) {
    item: updateEntry(payload: $payload) {
      id
      status
    }
  }
`;

const updateManyEntries = gql`
  mutation UpdateManyEntries($ids: [Int!], $payload: EntryPayload!) {
    items: updateManyEntries(ids: $ids, payload: $payload) {
      id
    }
  }
`;

const deleteEntry = gql`
  mutation DeleteEntry($id: Int!) {
    item: deleteEntry(id: $id) {
      id
    }
  }
`;

const deleteManyEntries = gql`
  mutation DeleteManyEntries($ids: [Int!]) {
    items: deleteManyEntries(ids: $ids) {
      id
    }
  }
`;

const getEventCompetitors = gql`
  query GetEventCompetitors($slug: String!) {
    items: getEventCompetitors(slug: $slug) {
      id
      name
      startTime
      color
      siCard
      user {
        id
      }
      device {
        id
        name
        imei
      }
      subEvent {
        name
      }
    }
    total: _getEventCompetitorsMeta(slug: $slug) {
      count
    }
  }
`;

const updateEventCompetitor = gql`
  mutation UpdateEventCompetitor($slug: String!, $id: Int!, $payload: CompetitorPayload!) {
    item: updateEventCompetitor(slug: $slug, id: $id, payload: $payload) {
      name
    }
  }
`;

const getMap = gql`
  query GetMap($subEventId: Int!, $id: Int!) {
    item: getMap(subEventId: $subEventId, id: $id) {
      id
      name
      width
      height
      url
      topLeft {
        lat
        lng
      }
      topRight {
        lat
        lng
      }
      bottomLeft {
        lat
        lng
      }
    }
  }
`;

const getMaps = gql`
  query GetMaps($subEventId: Int!) {
    items: getMaps(subEventId: $subEventId) {
      id
      name
      url
      createdAt
      show
    }
    total: _getMapsMeta(subEventId: $subEventId) {
      count
    }
  }
`;

const createMap = gql`
  mutation CreateMap($subEventId: Int!, $payload: CreateMapPayload!) {
    item: createMap(subEventId: $subEventId, payload: $payload) {
      name
    }
  }
`;

const updateMap = gql`
  mutation UpdateMap($subEventId: Int!, $id: Int, $payload: MapPayload!) {
    item: updateMap(subEventId: $subEventId, id: $id, payload: $payload) {
      name
    }
  }
`;

const deleteMap = gql`
  mutation DeleteMap($subEventId: Int!, $id: Int!) {
    item: deleteMap(subEventId: $subEventId, id: $id) {
      name
    }
  }
`;

const reorderMaps = gql`
  mutation ReorderMaps($subEventId: Int!, $payload: [ReorderPayload!]!) {
    items: reorderMaps(subEventId: $subEventId, payload: $payload) {
      name
    }
  }
`;

const getEventMaps = gql`
  query GetEventMaps($slug: String!) {
    items: getEventMaps(slug: $slug) {
      id
      name
      url
      createdAt
      show
    }
    total: _getEventMapsMeta(slug: $slug) {
      count
    }
  }
`;

const getEventMap = gql`
  query GetEventMap($slug: String!, $id: Int!) {
    item: getEventMap(slug: $slug, id: $id) {
      id
      name
      url
      height
      width
      show
      topLeft {
        lat
        lng
      }
      topRight {
        lat
        lng
      }
      bottomLeft {
        lat
        lng
      }
    }
  }
`;

const createEventMap = gql`
  mutation CreateEventMap($slug: String!, $payload: CreateMapPayload!) {
    item: createEventMap(slug: $slug, payload: $payload) {
      name
    }
  }
`;

const updateEventMap = gql`
  mutation UpdateEventMap($slug: String!, $id: Int, $payload: MapPayload!) {
    item: updateEventMap(slug: $slug, id: $id, payload: $payload) {
      name
    }
  }
`;

const deleteEventMap = gql`
  mutation DeleteEventMap($slug: String!, $id: Int!) {
    item: deleteEventMap(slug: $slug, id: $id) {
      name
    }
  }
`;

const reorderEventMaps = gql`
  mutation ReorderEventMaps($slug: String!, $payload: [ReorderPayload!]!) {
    items: reorderEventMaps(slug: $slug, payload: $payload) {
      name
    }
  }
`;

const getRoutes = gql`
  query GetRoutes($subEventId: Int!) {
    items: getRoutes(subEventId: $subEventId) {
      id
      name
      createdAt
      show
    }
    total: _getRoutesMeta(subEventId: $subEventId) {
      count
    }
  }
`;

const getRoute = gql`
  query GetRoute($subEventId: Int!, $id: Int!) {
    item: getRoute(subEventId: $subEventId, id: $id) {
      id
      name
      color
      show
      track {
        lat
        lng
      }
    }
  }
`;

const createRoute = gql`
  mutation CreateRoute($subEventId: Int!, $payload: RoutePayload!) {
    item: createRoute(subEventId: $subEventId, payload: $payload) {
      name
    }
  }
`;

const updateRoute = gql`
  mutation UpdateRoute($subEventId: Int!, $id: Int!, $payload: RoutePayload!) {
    item: updateRoute(subEventId: $subEventId, id: $id, payload: $payload) {
      name
    }
  }
`;

const deleteRoute = gql`
  mutation DeleteRoute($subEventId: Int!, $id: Int!) {
    item: deleteRoute(subEventId: $subEventId, id: $id) {
      name
    }
  }
`;

const reorderRoutes = gql`
  mutation ReorderRoutes($subEventId: Int!, $payload: [ReorderPayload!]!) {
    items: reorderRoutes(subEventId: $subEventId, payload: $payload) {
      name
    }
  }
`;

const getEntryData = gql`
  query GetEntryData($slug: String!) {
    items: getEntryData(slug: $slug) {
      email
      name
    }
  }
`;

const createManyEntryData = gql`
  mutation CreateManyEntryData($slug: String!, $payload: EntryJsonPayload) {
    items: createManyEntryData(slug: $slug, payload: $payload) {
      email
      name
    }
  }
`;

const deleteManyEntryData = gql`
  mutation DeleteManyEntryData($slug: String!, $ids: [String!]!) {
    items: deleteManyEntryData(slug: $slug, emails: $ids) {
      name
    }
  }
`;

export const GET_ALL_DEVICES = gql`
  query GetAllDevices($unit: Boolean) {
    allDevices(unit: $unit) {
      id
      name
      imei
      info
      unit
      battery
      last
      liveloxPersonId
      user {
        id
        firstName
        lastName
        uid
      }
    }
  }
`;

export const getAllDevices = gql`
  query GetAllDevicesList($unit: Boolean) {
    items: allDevices(unit: $unit) {
      id
      name
      imei
      unit
      battery
      last
      liveloxPersonId
      eventCount
      user {
        id
        firstName
        lastName
        uid
      }
      isVirtual
    }
  }
`;

const getDeviceEvents = gql`
  query GetDeviceEvents($id: Int!) {
    getDeviceEvents(id: $id) {
      event {
        id
        name
        slug
        startDate
        endDate
        status
      }
      subEvent {
        id
        name
        sub
      }
    }
  }
`;

export const getDevices = gql`
  query GetDevices {
    items: getDevices {
      id
      name
      last
      battery
      imei
      isVirtual
    }
    total: _getDevicesMeta {
      count
    }
  }
`;

const getDevice = gql`
  query GetDevice($id: Int!) {
    item: getDevice(id: $id) {
      id
      name
      imei
      info
      userId
    }
  }
`;

const createDevice = gql`
  mutation CreateDevice($payload: DevicePayload!) {
    item: createDevice(payload: $payload) {
      name
    }
  }
`;

const updateDevice = gql`
  mutation UpdateDevice($id: Int!, $payload: DevicePayload!) {
    item: updateDevice(id: $id, payload: $payload) {
      name
    }
  }
`;

const deleteDevice = gql`
  mutation DeleteDevice($id: Int!) {
    item: deleteDevice(id: $id) {
      name
    }
  }
`;

const deleteManyDevices = gql`
  mutation DeleteManyDevices($ids: [Int!]) {
    item: deleteManyDevices(ids: $ids) {
      name
    }
  }
`;

const mergeDevicesAllDevice = gql`
  mutation MergeDevicesAllDevice($ids: [Int!]!) {
    item: mergeDevices(ids: $ids) {
      id
      name
      liveloxPersonId
      imei
    }
  }
`;

const mapCompetitorsToDeviceUserAllDevice = gql`
  mutation MapCompetitorsToDeviceUserAllDevice($ids: [Int!]!) {
    item: mapCompetitorsToDeviceUser(ids: $ids) {
      updatedCount
    }
  }
`;

const createGroup = gql`
  mutation CreateGroup($payload: GroupPayload) {
    item: createGroup(payload: $payload) {
      name
    }
  }
`;

const updateGroup = gql`
  mutation UpdateGroup($id: Int!, $payload: GroupPayload) {
    item: updateGroup(id: $id, payload: $payload) {
      name
    }
  }
`;

const deleteGroup = gql`
  mutation DeleteGroup($id: Int!) {
    item: deleteGroup(id: $id) {
      name
    }
  }
`;

const getGroup = gql`
  query GetGroup($id: Int!) {
    item: getGroup(id: $id) {
      id
      name
      groupUsers {
        id
        firstName
        lastName
      }
    }
  }
`;

const getGroupUsers = gql`
  query GetGroupUsers($groupId: Int!) {
    items: getGroupUsers(id: $groupId) {
      id
      name
    }
  }
`;

const createManyGroupUsers = gql`
  mutation CreateManyGroupUsers($groupId: Int!, $payload: ManyGroupUserPayload!) {
    items: createManyGroupUsers(groupId: $groupId, payload: $payload) {
      name
    }
  }
`;

const deleteGroupUser = gql`
  mutation DeleteGroupUser($groupId: Int!, $id: Int) {
    item: deleteGroupUser(groupId: $groupId, id: $id) {
      name
    }
  }
`;

const deleteManyGroupUsers = gql`
  mutation DeleteManyGroupUsers($groupId: Int!, $ids: [Int]) {
    items: deleteManyGroupUsers(groupId: $groupId, ids: $ids) {
      name
    }
  }
`;

export const getUsers = gql`
  query GetUsersAndDevices {
    items: getUsers {
      id
      firstName
      lastName
      name
      city
      club
      siCard
      preferredDevices {
        shortName
        color
        device {
          id
          imei
          name
        }
      }
      userDevice {
        id
        imei
        name
      }
    }
    getDevices {
      id
      name
      isVirtual
    }
  }
`;

export const SAVE_PREFERRED_DEVICE = gql`
  mutation SavePreferredDevice($userId: Int!, $payload: PreferredDevicePayload!) {
    savePreferredDevice(userId: $userId, payload: $payload) {
      shortName
      color
      device {
        id
        imei
        name
      }
    }
  }
`;

export const DELETE_PREFERRED_DEVICE = gql`
  mutation DeletePreferredDevice($userId: Int!) {
    deletePreferredDevice(userId: $userId) {
      shortName
    }
  }
`;

export const DASHBOARD_QUERY = gql`
  query DashboardQuery($page: Int!, $size: Int!) {
    myEvents(page: $page, size: $size) {
      name
      slug
      status
      startDate
      endDate
    }
    waitingEntries {
      id
      createdAt
      user {
        firstName
        lastName
      }
      event {
        slug
        name
      }
    }
  }
`;

const getLogos = gql`
  query getLogos($slug: String!) {
    items: getLogos(slug: $slug) {
      id
      name
      url
      show
    }
  }
`;

const createLogo = gql`
  mutation CreateLogo($slug: String!, $payload: LogoPayload!) {
    item: createLogo(slug: $slug, payload: $payload) {
      name
    }
  }
`;

const updateLogo = gql`
  mutation UpdateLogo($slug: String!, $id: Int!, $payload: LogoPayload!) {
    item: updateLogo(slug: $slug, id: $id, payload: $payload) {
      name
    }
  }
`;

const deleteLogo = gql`
  mutation DeleteLogo($slug: String!, $id: Int!) {
    item: deleteLogo(slug: $slug, id: $id) {
      name
    }
  }
`;

const getLogs = gql`
  query GetLogs($page: Int, $size: Int) {
    items: getLogs(page: $page, size: $size) {
      id
      name
      time
    }
    total: _getLogsMeta {
      count
    }
  }
`;

const getLog = gql`
  query getLog($id: String!) {
    item: getLog(id: $id) {
      id
      name
      time
      data {
        level
        timestamp
        message
        meta
      }
    }
  }
`;

const getConfigurations = gql`
  query GetConfigurations {
    items: getConfigurations {
      id
      name
      value
      type
      isVisible
      isEditable
      description
    }
  }
`;

const updateConfiguration = gql`
  mutation UpdateConfiguration($id: Int!, $payload: ConfigurationInput!) {
    item: updateConfiguration(id: $id, payload: $payload) {
      name
    }
  }
`;

const clearAllCache = gql`
  mutation ClearAllCache {
    clearAllCache
  }
`;

const pullCorosActivities = gql`
  mutation PullCorosActivities {
    pullCorosActivities
  }
`;

export const getRoles = gql`
  query GetRoles {
    items: getRoles {
      id
      name
      isSystem
      isPermissionsEditable
    }
  }
`;

const getRole = gql`
  query GetRole($id: Int!) {
    item: getRole(id: $id) {
      id
      name
      isSystem
      isPermissionsEditable
      permissions {
        granted
        permission {
          id
          resource
          action
          key
          description
        }
      }
    }
  }
`;

const createRole = gql`
  mutation CreateRole($payload: RolePayload!) {
    item: createRole(payload: $payload) {
      id
      name
    }
  }
`;

const updateRole = gql`
  mutation UpdateRole($id: Int!, $payload: RolePayload!) {
    item: updateRole(id: $id, payload: $payload) {
      id
      name
    }
  }
`;

const deleteRole = gql`
  mutation DeleteRole($id: Int!) {
    item: deleteRole(id: $id) {
      id
      name
    }
  }
`;

const getPermissionResources = gql`
  query GetPermissionResources {
    getPermissionResources {
      resource
      description
      actions {
        action
        description
        key
        permissionId
      }
    }
  }
`;

const updateRolePermissions = gql`
  mutation UpdateRolePermissions($roleId: Int!, $permissions: [RolePermissionInput!]!) {
    item: updateRolePermissions(roleId: $roleId, permissions: $permissions) {
      id
      name
    }
  }
`;

const syncActivityCompetitorsEvent = gql`
  mutation syncActivityCompetitors($id: String!) {
    item: syncActivityCompetitors(slug: $id) {
      created
      skipped
    }
  }
`;

const extractManyTracksCompetitor = gql`
  mutation extractManyTracksCompetitor($subEventId: Int!) {
    items: extractManyTracksCompetitor(subEventId: $subEventId) {
      id
      name
    }
  }
`;

const bulkUpdateCompetitorTracksCompetitor = gql`
  mutation BulkUpdateCompetitorTracks($subEventId: Int!, $ids: [Int!]!) {
    bulkUpdateCompetitorTracks(subEventId: $subEventId, ids: $ids) {
      id
      name
    }
  }
`;

export const GET_EVENTS_STATUS_COUNTS = getEventsStatusCounts;

export const queries: { [key: string]: DocumentNode } = {
  clearAllCache,
  pullCorosActivities,
  getEvents,
  getEventsStatusCounts,
  getEvent,
  createEvent,
  updateEvent,
  deleteEvent,
  getEventSettings,
  updateEventSettings,
  getSubEvents,
  createSubEvent,
  updateSubEvent,
  deleteSubEvent,
  getSubEvent,
  getControl,
  getControls,
  createControl,
  createManyControls,
  deleteControl,
  deleteManyControls,
  updateControl,
  updateManyControls,
  reorderControls,
  getSubEventControls,
  updateManySubEventControls,
  mergeControl,
  getAllUsers,
  getUserRecentActivities,
  updateAllUser,
  getGroups,
  getUsers,
  getCompetitorTrack,
  createCompetitorTrack,
  updateCompetitorTrack,
  deleteCompetitorTrack,
  extractManyTracksCompetitor,
  syncActivityCompetitorsEvent,
  bulkUpdateCompetitorTracksCompetitor,
  getCompetitor,
  getCompetitors,
  createCompetitor,
  createManyCompetitors,
  updateCompetitor,
  deleteCompetitor,
  deleteManyCompetitors,
  reorderCompetitors,
  updateCompetitorPrivateCompetitor,
  getEntries,
  updateEntry,
  updateManyEntries,
  deleteEntry,
  deleteManyEntries,
  getEventCompetitors,
  updateEventCompetitor,
  getMap,
  getMaps,
  createMap,
  updateMap,
  deleteMap,
  reorderMaps,
  getEventMap,
  getEventMaps,
  createEventMap,
  updateEventMap,
  deleteEventMap,
  reorderEventMaps,
  getRoutes,
  getRoute,
  createRoute,
  updateRoute,
  deleteRoute,
  reorderRoutes,
  getEntryData,
  createManyEntryData,
  deleteManyEntryData,
  GET_ALL_DEVICES,
  getAllDevices,
  getDeviceEvents,
  getDevices,
  createDevice,
  updateDevice,
  deleteDevice,
  deleteManyDevices,
  mergeDevicesAllDevice,
  mapCompetitorsToDeviceUserAllDevice,
  getDevice,
  createGroup,
  updateGroup,
  deleteGroup,
  getGroup,
  getGroupUsers,
  createManyGroupUsers,
  deleteGroupUser,
  deleteManyGroupUsers,
  SAVE_PREFERRED_DEVICE,
  DELETE_PREFERRED_DEVICE,
  DASHBOARD_QUERY,
  getLogos,
  createLogo,
  updateLogo,
  deleteLogo,
  getLogs,
  getLog,
  getConfigurations,
  updateConfiguration,
  getRoles,
  getRole,
  createRole,
  updateRole,
  deleteRole,
  getPermissionResources,
  updateRolePermissions
};

export default queries;
